import { z } from "zod";
import { paginatedSchema, paginationQuerySchema } from "../common/pagination.js";
import { idSchema, isoDateTimeSchema, userSummarySchema } from "../common/primitives.js";
import { workspaceParamsSchema } from "../workspaces/workspace.schemas.js";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/**
 * PUBLIC: any non-guest member can find and join it. PRIVATE: invite only.
 * PROJECT: created with a project; its members are the project's members.
 * DIRECT: a one-to-one conversation.
 */
export const CHANNEL_TYPES = ["PUBLIC", "PRIVATE", "PROJECT", "DIRECT"] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];
export const channelTypeSchema = z.enum(CHANNEL_TYPES);

export const MAX_MESSAGE_LENGTH = 4000;
export const MAX_CHANNEL_MEMBERS_PER_REQUEST = 100;

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const channelSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  type: channelTypeSchema,
  /** Null for direct messages: show the other person's name instead. */
  name: z.string().nullable(),
  description: z.string().nullable(),
  /** Set for PROJECT channels. */
  projectId: idSchema.nullable(),
  /** The other participant of a DIRECT channel. */
  peer: userSummarySchema.nullable(),
  memberCount: z.number().int().nonnegative(),
  /** Whether the caller belongs to the channel (false only when browsing public channels). */
  joined: z.boolean(),
  /** Whether the caller may rename, delete, or remove people from the channel. */
  canManage: z.boolean(),
  /** Messages from other people after the caller's read marker. */
  unreadCount: z.number().int().nonnegative(),
  /** Unread messages that mention the caller. */
  mentionCount: z.number().int().nonnegative(),
  /** Whether the caller muted the channel: its unread messages stop counting towards badges. */
  muted: z.boolean(),
  lastMessageAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type Channel = z.infer<typeof channelSchema>;

export const channelMemberSchema = z.object({
  userId: idSchema,
  joinedAt: isoDateTimeSchema,
  user: userSummarySchema,
});
export type ChannelMember = z.infer<typeof channelMemberSchema>;

export const messageReactionSchema = z.object({
  emoji: z.string(),
  /** Everyone who reacted with this emoji, oldest reaction first. */
  userIds: z.array(idSchema),
});
export type MessageReaction = z.infer<typeof messageReactionSchema>;

export const messageSchema = z.object({
  id: idSchema,
  channelId: idSchema,
  /** Monotonic position in the channel; used for ordering and read markers. */
  seq: z.number().int().positive(),
  /** Null once the author has left the workspace. */
  author: userSummarySchema.nullable(),
  body: z.string(),
  /** Set for replies: the top-level message of the thread they belong to. */
  parentMessageId: idSchema.nullable(),
  replyCount: z.number().int().nonnegative(),
  lastReplyAt: isoDateTimeSchema.nullable(),
  reactions: z.array(messageReactionSchema),
  /** Channel members mentioned in the body (`<@userId>`). */
  mentionedUserIds: z.array(idSchema),
  pinnedAt: isoDateTimeSchema.nullable(),
  editedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type Message = z.infer<typeof messageSchema>;

/** The slice of a channel a search result needs to say where a message was written. */
export const messageChannelRefSchema = z.object({
  id: idSchema,
  type: channelTypeSchema,
  name: z.string().nullable(),
  peer: userSummarySchema.nullable(),
});
export type MessageChannelRef = z.infer<typeof messageChannelRefSchema>;

export const messageSearchResultSchema = z.object({
  message: messageSchema,
  channel: messageChannelRefSchema,
});
export type MessageSearchResult = z.infer<typeof messageSearchResultSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** Lowercase channel names without a leading `#`, like `design-team`. */
export const channelNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Name is required")
  .max(60, "Name must be at most 60 characters")
  .regex(/^[\p{L}\p{N}][\p{L}\p{N}_-]*$/u, "Use letters, numbers, hyphens, and underscores");

const channelDescriptionSchema = z
  .string()
  .trim()
  .max(500, "Description must be at most 500 characters");

export const createChannelSchema = z.object({
  name: channelNameSchema,
  description: channelDescriptionSchema.optional(),
  isPrivate: z.boolean().optional(),
  /** Workspace members added next to the creator. */
  memberIds: z.array(idSchema).max(MAX_CHANNEL_MEMBERS_PER_REQUEST).optional(),
});
export type CreateChannelInput = z.infer<typeof createChannelSchema>;

export const updateChannelSchema = z
  .object({
    name: channelNameSchema.optional(),
    description: channelDescriptionSchema.nullable().optional(),
  })
  .refine((value) => Object.values(value).some((v) => v !== undefined), {
    error: "Provide at least one field to update",
  });
export type UpdateChannelInput = z.infer<typeof updateChannelSchema>;

export const openDirectMessageSchema = z.object({ userId: idSchema });
export type OpenDirectMessageInput = z.infer<typeof openDirectMessageSchema>;

export const addChannelMemberSchema = z.object({ userId: idSchema });
export type AddChannelMemberInput = z.infer<typeof addChannelMemberSchema>;

export const messageBodySchema = z
  .string()
  .trim()
  .min(1, "Write a message first")
  .max(MAX_MESSAGE_LENGTH, `Messages can be at most ${MAX_MESSAGE_LENGTH} characters`);

export const sendMessageSchema = z.object({
  body: messageBodySchema,
  /** Reply in the thread of this top-level message. */
  parentMessageId: idSchema.optional(),
});
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const updateMessageSchema = z.object({ body: messageBodySchema });
export type UpdateMessageInput = z.infer<typeof updateMessageSchema>;

/** Without `parentMessageId` the channel's top-level messages; with it, that thread's replies. */
export const listMessagesQuerySchema = paginationQuerySchema.extend({
  parentMessageId: idSchema.optional(),
});
export type ListMessagesQuery = z.infer<typeof listMessagesQuerySchema>;
export interface ListMessagesQueryInput {
  limit?: number | undefined;
  cursor?: string | undefined;
  parentMessageId?: string | undefined;
}

export const MAX_REACTIONS_PER_MESSAGE = 20;
export const MAX_EMOJI_LENGTH = 16;
/** Emoji offered first in the reaction picker; any emoji can be used. */
export const QUICK_REACTIONS = ["👍", "❤\ufe0f", "😂", "🎉", "😮", "😢", "🙏", "👀"] as const;
/** One emoji: pictographs joined by zero-width joiners, with variation selectors and skin tones. */
export const reactionEmojiSchema = z
  .string()
  .max(MAX_EMOJI_LENGTH, "Pick a single emoji")
  .regex(
    /^\p{Extended_Pictographic}(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|\u200d|\ufe0f)*$/u,
    "Pick a single emoji",
  );
export const addReactionSchema = z.object({ emoji: reactionEmojiSchema });
export type AddReactionInput = z.infer<typeof addReactionSchema>;

export const muteChannelSchema = z.object({ muted: z.boolean() });
export type MuteChannelInput = z.infer<typeof muteChannelSchema>;

export const MIN_SEARCH_LENGTH = 2;
export const searchMessagesQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(MIN_SEARCH_LENGTH).max(100),
});
export type SearchMessagesQuery = z.infer<typeof searchMessagesQuerySchema>;
export interface SearchMessagesQueryInput {
  q: string;
  limit?: number | undefined;
  cursor?: string | undefined;
}

export const channelParamsSchema = workspaceParamsSchema.extend({ channelId: idSchema });
export const channelMemberParamsSchema = channelParamsSchema.extend({ userId: idSchema });
export const messageParamsSchema = channelParamsSchema.extend({ messageId: idSchema });
export const messageReactionParamsSchema = messageParamsSchema.extend({
  emoji: reactionEmojiSchema,
});

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/**
 * Optional paging for lists that used to return everything. Without `limit` the generous default
 * applies; `nextCursor` in the response is non-null when more remain.
 */
export const DEFAULT_LIST_LIMIT = 200;
export const MAX_LIST_LIMIT = 500;
export const listAllQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_LIST_LIMIT).default(DEFAULT_LIST_LIMIT),
  cursor: z.string().min(1).max(512).optional(),
});
export type ListAllQuery = z.infer<typeof listAllQuerySchema>;
export interface ListAllQueryInput {
  limit?: number | undefined;
  cursor?: string | undefined;
}

export const channelsResponseSchema = z.object({
  channels: z.array(channelSchema),
  /** Present when the list was cut at `limit`; pass it as `cursor` for the rest. */
  nextCursor: z.string().nullable().optional(),
});
export type ChannelsResponse = z.infer<typeof channelsResponseSchema>;

export const channelResponseSchema = z.object({ channel: channelSchema });
export type ChannelResponse = z.infer<typeof channelResponseSchema>;

export const channelMembersResponseSchema = z.object({ members: z.array(channelMemberSchema) });
export type ChannelMembersResponse = z.infer<typeof channelMembersResponseSchema>;

/** Newest first; `nextCursor` continues towards older messages. */
export const messagesResponseSchema = paginatedSchema(messageSchema);
export type MessagesResponse = z.infer<typeof messagesResponseSchema>;

export const messageResponseSchema = z.object({ message: messageSchema });
export type MessageResponse = z.infer<typeof messageResponseSchema>;

export const pinnedMessagesResponseSchema = z.object({
  messages: z.array(messageSchema),
  nextCursor: z.string().nullable().optional(),
});
export type PinnedMessagesResponse = z.infer<typeof pinnedMessagesResponseSchema>;

/** Newest first; `nextCursor` continues towards older matches. */
export const messageSearchResponseSchema = paginatedSchema(messageSearchResultSchema);
export type MessageSearchResponse = z.infer<typeof messageSearchResponseSchema>;

/** Workspace members who currently have the app open. */
export const presenceResponseSchema = z.object({
  userIds: z.array(idSchema),
  nextCursor: z.string().nullable().optional(),
});
export type PresenceResponse = z.infer<typeof presenceResponseSchema>;
